import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, ExternalLink } from "lucide-react";
import { cloudinaryThumb } from "@/lib/cloudinary";
import PhotoLightbox from "@/components/PhotoLightbox";
import { toast } from "sonner";

const CIERRE = {
  montado: { label: "Montado", cls: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" },
  semi_montado: { label: "Semi montado", cls: "bg-amber-500/20 text-amber-300 border-amber-500/40" },
  incidencia: { label: "Incidencia", cls: "bg-red-500/20 text-red-300 border-red-500/40" },
  cancelado: { label: "Cancelado", cls: "bg-zinc-700/40 text-zinc-300 border-zinc-600" },
};
const TIPO_INC = { tiempo: "⏰ Tiempo", medida: "📐 Medida", otros: "❓ Otros" };

function fechaLarga(v) {
  const iso = String(v.fecha_montaje_real || v.finalizado_at || v.ruta_fecha || "").slice(0, 10);
  if (!iso) return "—";
  return new Date(iso + "T00:00:00").toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

function VisitaBlock({ v, num, total, onFoto }) {
  const c = CIERRE[v.estado_cierre] || CIERRE.cancelado;
  const fotos = [v.foto_previa, ...(v.fotos_finales || [])].filter(Boolean);
  const descargarPdf = async () => {
    try {
      const r = await api.get(`/rutas/${v.ruta_id}/paradas/${v.idx}/incidencia.pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch (err) { toast.error("Error generando el PDF de incidencia"); }
  };
  return (
    <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-3 space-y-2" data-testid={`reporte-visita-${v.ruta_id}-${v.idx}`}>
      <div className="flex items-center gap-2 flex-wrap text-xs">
        {total > 1 && <span className="text-zinc-500 font-mono">Visita {num}/{total}</span>}
        <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded border font-bold ${c.cls}`}>{c.label}</span>
        {v.tipo_incidencia && <span className="text-[10px] px-2 py-0.5 rounded bg-zinc-800 text-zinc-300">{TIPO_INC[v.tipo_incidencia] || v.tipo_incidencia}</span>}
        <span className="text-zinc-400">📅 {fechaLarga(v)}</span>
        <span className="text-zinc-500">· {v.ruta_codigo}</span>
        {v.montadores?.length > 0 && <span className="text-zinc-400">· 🔧 {v.montadores.join(", ")}</span>}
      </div>
      {v.incidencia_descripcion && (
        <div className="bg-red-500/10 border border-red-500/25 rounded p-2 text-xs text-red-200 whitespace-pre-wrap">
          {v.incidencia_descripcion}
        </div>
      )}
      {fotos.length === 0 ? (
        <div className="text-xs text-zinc-500">Sin fotos en esta visita.</div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5">
          {fotos.map((f, i) => (
            <button key={f.id || i} onClick={() => onFoto(fotos, i)}
              data-testid={`reporte-foto-${v.ruta_id}-${v.idx}-${i}`}
              className="relative aspect-square rounded overflow-hidden border border-zinc-800 hover:border-cyan-500/60 transition-colors">
              <img src={cloudinaryThumb(f.url, 200)} alt={f.categoria || "foto"} className="w-full h-full object-cover" loading="lazy" />
              {f.categoria === "previa" && (
                <span className="absolute bottom-0 inset-x-0 bg-black/70 text-[8px] uppercase text-zinc-300 text-center">previa</span>
              )}
            </button>
          ))}
        </div>
      )}
      {v.estado_cierre !== "montado" && (
        <Button size="sm" variant="outline" onClick={descargarPdf}
          data-testid={`reporte-pdf-incidencia-${v.ruta_id}-${v.idx}`}
          className="bg-transparent border-amber-600/40 text-amber-300 hover:bg-amber-500/10 h-7 text-xs">
          <Download className="w-3.5 h-3.5 mr-1.5" /> PDF de incidencia
        </Button>
      )}
    </div>
  );
}

export default function ReporteMontajeModal({ data, onClose }) {
  const nav = useNavigate();
  const { visitas, espacioNombre } = data;
  const [lightbox, setLightbox] = useState(null); // { fotos, idx }
  return (
    <>
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 w-[95vw] sm:w-auto max-w-[720px] max-h-[88vh] overflow-y-auto p-4"
          data-testid="reporte-montaje-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 flex-wrap text-base">
              📷 Reporte de montaje {espacioNombre ? <span className="text-zinc-400 font-normal">· {espacioNombre}</span> : null}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {visitas.map((v, i) => (
              <VisitaBlock key={`${v.ruta_id}-${v.idx}`} v={v} num={i + 1} total={visitas.length}
                onFoto={(fotos, idx) => setLightbox({ fotos, idx })} />
            ))}
            <Button variant="outline" onClick={() => nav("/reportes")}
              data-testid="reporte-abrir-en-reportes"
              className="w-full bg-transparent border-zinc-700 text-zinc-300 hover:text-zinc-100 hover:border-zinc-500">
              <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Abrir en Reportes (aprobar fotos, enviar al cliente...)
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      {lightbox && (
        <PhotoLightbox fotos={lightbox.fotos} openIdx={lightbox.idx}
          onClose={() => setLightbox(null)} title={espacioNombre || "Reporte de montaje"} />
      )}
    </>
  );
}
